// Router-free URL state. We reflect the current page in query params and write
// them with history.replaceState so a browser refresh restores the same page
// instead of dropping the user on Home. replaceState (not pushState) means we
// never add history entries, so the browser Back button keeps its existing
// behavior. Query params (not path segments) keep this working on static
// hosting without SPA rewrite config. This generalizes the ?audit=<id> pattern
// built for the Audit Report across every level of the app's nested navigation:
//   ?view=<stage>  top-level view   (App.js)
//   ?tab=<tab>     admin sub-tab    (AdminDashboard)
//   ?sub=<view>    learning sub-view(LearningEngine)

export const getParam = (key) => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(key);
};

// Branded invite links use a PATH segment (https://audee.io/i/<code>) so they
// read as a clean, professional URL. A Vercel rewrite (frontend/vercel.json)
// serves index.html for /i/* so direct-nav and hard-refresh resolve to the SPA;
// this reads the <code> back out of the path. Returns null when the current URL
// is not a branded invite link.
export const getInviteCodeFromPath = () => {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/i\/([A-Za-z0-9]+)\/?$/);
  return m ? m[1] : null;
};

// After a branded invite is handled, replace the /i/<code> path with '/' (no
// history entry) so a refresh doesn't re-open the invite screen — the persisted
// Supabase session keeps the user in the app across reloads.
export const clearInvitePath = () => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/i/')) {
    window.history.replaceState(window.history.state, '', '/' + window.location.search + window.location.hash);
  }
};

// Push a top-level view as a real browser history entry so the Back button steps
// back through in-app views (design-partner request 2026-07-17) instead of
// leaving the app on the first press. Only the top-level ?view uses pushState;
// deeper levels (?tab/?sub) still use setParams/replaceState so we never spam the
// history stack. `replace: true` writes the initial view without adding an entry.
export const pushView = (view, { replace = false } = {}) => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (view == null || view === '') url.searchParams.delete('view');
  else url.searchParams.set('view', String(view));
  const fn = replace ? 'replaceState' : 'pushState';
  window.history[fn]({ ...(window.history.state || {}), view: view || null }, '', url);
};

// Read the top-level view from history state or the ?view param (null if absent).
export const getView = () => {
  if (typeof window === 'undefined') return null;
  if (window.history.state && window.history.state.view) return window.history.state.view;
  return getParam('view');
};

// Apply a set of param updates in one replaceState. A null/'' value deletes the
// param — callers clear deeper-level params (e.g. tab/sub) when navigating away.
export const setParams = (updates) => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  Object.entries(updates).forEach(([k, v]) => {
    if (v == null || v === '') url.searchParams.delete(k);
    else url.searchParams.set(k, String(v));
  });
  window.history.replaceState(window.history.state, '', url);
};
